import { proxyCustomElement, HTMLElement, createEvent, h, Fragment, Host } from '@stencil/core/internal/client';
import { e as emitEvent, d as isNumber } from './utils.js';
import { d as defineCustomElement$1 } from './cl-icon2.js';

// Constants
const component = 'cl-data-table-header-cell';

const clDataTableHeadCellCss = ":host(cl-data-table-head-cell){--line-width:var(--border-width);--line-color:rgb(var(--color-neutral-200));--grab-color:rgb(var(--color-neutral-200));--drop-indicator-color:rgb(var(--color-brand-600));box-sizing:border-box;display:table-cell;font-weight:bold;height:100%;max-width:var(--data-table-column-width);min-width:var(--data-table-column-width);padding:var(--data-table-cell-padding, var(--data-table-padding, inherit));position:relative;text-wrap:wrap;vertical-align:middle;width:var(--data-table-column-width)}@supports (background: -webkit-named-image(i)){:host(cl-data-table-head-cell){border-bottom:var(--border-width) solid var(--data-table-border-color);transition:border-color var(--animation-duration-fast) var(--animation-timing-easeinout)}}:host(cl-data-table-head-cell) .content{align-items:center;display:flex;gap:var(--spacing-1);overflow:hidden}:host(cl-data-table-head-cell) .handle--drag{-webkit-user-drag:element}:host(cl-data-table-head-cell) .handle--drag svg{color:var(--grab-color);transition:color var(--animation-duration-fast) ease-in-out}:host(cl-data-table-head-cell) .handle--drag:hover{--grab-color:rgb(var(--color-brand-600));cursor:grab}:host(cl-data-table-head-cell) .handle--drag:active{cursor:grabbing}:host(cl-data-table-head-cell) .handle--resize{cursor:col-resize;height:100%;position:absolute;right:calc(-1 * var(--line-width) / 2);top:0;width:calc(var(--line-width) * 2);z-index:1}:host(cl-data-table-head-cell) .handle--resize:hover{--line-color:rgb(var(--color-brand-600))}:host(cl-data-table-head-cell) .handle--resize::before{background-color:var(--line-color);border-radius:var(--border-radius-small);content:\"\";height:calc(100% - var(--data-table-cell-padding, var(--data-table-padding, inherit)));left:50%;position:absolute;top:50%;transform:translate(-50%, -50%);transition:background-color var(--animation-duration-fast) ease-in-out;width:var(--line-width);z-index:var(--z-index-9)}:host(cl-data-table-head-cell.dragging){background-color:rgb(var(--color-brand-100))}:host(cl-data-table-head-cell.drop-overlay){position:relative}:host(cl-data-table-head-cell.drop-overlay)::after{background-color:var(--drop-overlay-color);border:none;content:\"\";display:block;height:var(--data-table-height);inset:0;outline:none;position:absolute;z-index:var(--z-index-10)}:host(cl-data-table-head-cell) .drop-indicator{--drop-indicator-width:var(--size-1);background-color:rgba(var(--color-brand-100), var(--opacity-4));border:none;display:none;height:var(--data-table-height);outline:var(--line-width) dashed rgb(var(--color-brand-600));position:absolute;top:0;width:var(--drop-indicator-width);z-index:var(--z-index-10)}:host(cl-data-table-head-cell) .drop-indicator--left{left:calc(var(--drop-indicator-width) / 2 * -1)}:host(cl-data-table-head-cell) .drop-indicator--right{right:calc(var(--drop-indicator-width) / 2 * -1)}:host(cl-data-table-head-cell.drop-overlay.drop-overlay--hover.drop-overlay--hover--left) .drop-indicator--left{display:block}:host(cl-data-table-head-cell.drop-overlay.drop-overlay--hover.drop-overlay--hover--right) .drop-indicator--right{display:block}";

const ClDataTableHeadCell = /*@__PURE__*/ proxyCustomElement(class ClDataTableHeadCell extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.clDragEnd = createEvent(this, "clDragEnd", 7);
    this.clDragStart = createEvent(this, "clDragStart", 7);
    this.clDrop = createEvent(this, "clDrop", 7);
    this.initialWidth = 0;
    this.isResizeDragging = false;
    this.mousePositionX = 0;
    this.handleToggleDropOverlay = (event) => {
      const target = event.target;
      if (target !== this.element && this.rearrange && event.type === 'clDragStart') {
        this.element.classList.add('drop-overlay');
      }
    };
    this.handleMouseDown = (event) => {
      this.mousePositionX =
        event.type === 'mousedown' ? event.clientX : event.touches[0].pageX;
      this.isResizeDragging = true;
      this.initialWidth = this.element.clientWidth;
    };
    this.handleDragStart = (event) => {
      var _a;
      if (event.dataTransfer) {
        (_a = event.dataTransfer) === null || _a === void 0 ? void 0 : _a.setDragImage(this.element, 0, 0);
        event.dataTransfer.setData('text/plain', `${this.cellIndex}`);
        this.element.classList.add('dragging');
        emitEvent(this, 'clDragStart', { component, originalEvent: event, dragIndex: this.cellIndex });
      }
    };
    this.handleDragEnd = (event) => {
      emitEvent(this, 'clDragEnd', { component, originalEvent: event, dragIndex: this.cellIndex });
    };
    this.handleDrop = (event) => {
      var _a, _b, _c, _d, _e;
      event.preventDefault();
      const dragIndex = (_a = event.dataTransfer) === null || _a === void 0 ? void 0 : _a.getData('text/plain');
      const isLeft = this.dropPosition === 'left';
      const dropIndexLeft = +(isLeft
        ? (_c = (_b = this.element.previousElementSibling) === null || _b === void 0 ? void 0 : _b.getAttribute('cell-index')) !== null && _c !== void 0 ? _c : '-1'
        : this.cellIndex);
      const dropIndexRight = isLeft
        ? this.cellIndex
        : (_d = this.element.nextElementSibling) === null || _d === void 0 ? void 0 : _d.getAttribute('cell-index');
      emitEvent(this, 'clDrop', {
        component,
        originalEvent: event,
        dragIndex: +dragIndex,
        dropIndexLeft,
        dropIndexRight: isNumber(dropIndexRight) ? +dropIndexRight : null,
      });
      (_e = event.dataTransfer) === null || _e === void 0 ? void 0 : _e.clearData();
    };
    /**
     * Initializes the drag event and adds the drop-overlay--hover class.
     */
    this.handleDragEnter = (event) => {
      event.preventDefault();
      if (this.element.classList.contains('drop-overlay')) {
        this.element.classList.add('drop-overlay--hover');
      }
    };
    /**
     * Removes the dragging-left/right className and the drop-overlay--hover class.
     */
    this.handleDragLeave = () => {
      this.element.classList.remove('drop-overlay--hover', 'drop-overlay--hover--left', 'drop-overlay--hover--right');
    };
    /**
     * Updates the dragging-left/right className based on the position of the dragged element.
     */
    this.handleDragOver = (event) => {
      event.preventDefault();
      const isLeft = (event.offsetX || event.layerX) < this.element.clientWidth / 2;
      this.dropPosition = isLeft ? 'left' : 'right';
      this.element.classList.toggle('drop-overlay--hover--left', isLeft);
      this.element.classList.toggle('drop-overlay--hover--right', !isLeft);
    };
    this._handleClick = (event) => {
      if (event.detail === 2) {
        this.element.style.setProperty('--data-table-column-width', 'auto');
      }
    };
    /**
     * Returns the draggable icon from BL
     */
    this.renderRearrangeIcon = () => (h("svg", { id: 'editor--drag', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' }, h("path", { d: 'M 14.499 15.9981 C 15.327 15.9981 15.999 16.6701 15.999 17.4981 S 15.327 18.9981 14.499 18.9981 12.999 18.3261 12.999 17.4981 13.671 15.9981 14.499 15.9981 Z M 9.5 15.9981 C 10.328 15.9981 11 16.6701 11 17.4981 S 10.328 18.9981 9.5 18.9981 8 18.3261 8 17.4981 8.672 15.9981 9.5 15.9981 Z M 14.499 10.999 C 15.327 10.999 15.999 11.671 15.999 12.499 S 15.327 13.999 14.499 13.999 12.999 13.327 12.999 12.499 13.671 10.999 14.499 10.999 Z M 9.5 10.999 C 10.328 10.999 11 11.671 11 12.499 S 10.328 13.999 9.5 13.999 8 13.327 8 12.499 8.672 10.999 9.5 10.999 Z M 14.499 6 C 15.327 6 15.999 6.672 15.999 7.5 S 15.327 9 14.499 9 12.999 8.328 12.999 7.5 13.671 6 14.499 6 Z M 9.5 6 C 10.328 6 11 6.672 11 7.5 S 10.328 9 9.5 9 8 8.328 8 7.5 8.672 6 9.5 6 Z', "fill-rule": 'evenodd' })));
    // This ensures that the slot will not be unmounted when the rearrange attribute changes.
    this.renderSlot = () => h("slot", { key: 'table-head-cell-slot' });
    this.renderContent = () => this.rearrange ? (h(Fragment, null, h("span", { class: 'drop-indicator drop-indicator--left' }), h("span", { class: 'content', draggable: true, onDragEnd: this.handleDragEnd, onDragStart: this.handleDragStart }, h("cl-icon", { class: 'handle--drag' }, this.renderRearrangeIcon()), this.renderSlot()), h("span", { class: 'drop-indicator drop-indicator--right' }))) : (h("span", { class: 'content' }, this.renderSlot()));
    this.cellIndex = undefined;
    this.resize = false;
    this.rearrange = false;
  }
  /**
   * Every time the component is connected to the DOM:
   * - Adds eventlisteners;
   */
  connectedCallback() {
    var _a, _b, _c;
    this.parentTableHeadRow = this.element.closest('cl-data-table-head-row');
    (_a = this.parentTableHeadRow) === null || _a === void 0 ? void 0 : _a.addEventListener('clDragEnd', this.handleToggleDropOverlay);
    (_b = this.parentTableHeadRow) === null || _b === void 0 ? void 0 : _b.addEventListener('clDragStart', this.handleToggleDropOverlay);
    (_c = this.parentTableHeadRow) === null || _c === void 0 ? void 0 : _c.addEventListener('clDrop', this.handleToggleDropOverlay);
  }
  /**
   * Every time the component is disconnected from the DOM:
   * - Removes eventlisteners;
   */
  disconnectedCallback() {
    var _a, _b, _c;
    (_a = this.parentTableHeadRow) === null || _a === void 0 ? void 0 : _a.removeEventListener('clDragEnd', this.handleToggleDropOverlay);
    (_b = this.parentTableHeadRow) === null || _b === void 0 ? void 0 : _b.removeEventListener('clDragStart', this.handleToggleDropOverlay);
    (_c = this.parentTableHeadRow) === null || _c === void 0 ? void 0 : _c.removeEventListener('clDrop', this.handleToggleDropOverlay);
  }
  /**
   * Document
   */
  handleMouseMove(event) {
    if (this.resize && this.isResizeDragging) {
      event.preventDefault();
      const mouseMovedX = (event.type === 'mousemove' ? event.clientX : event.touches[0].pageX) -
        this.mousePositionX;
      const newWidth = this.initialWidth + mouseMovedX;
      if (newWidth >= 0) {
        this.element.style.setProperty('--data-table-column-width', `${newWidth}px`);
      }
    }
  }
  /**
   * Handles mouse up
   */
  handleMouseUp() {
    this.isResizeDragging = false;
  }
  render() {
    const { rearrange, resize } = this;
    return (h(Host, { onDragEnter: rearrange && this.handleDragEnter, onDragLeave: rearrange && this.handleDragLeave, onDragOver: rearrange && this.handleDragOver, onDrop: rearrange && this.handleDrop }, this.renderContent(), resize && (h("span", { class: 'handle--resize', onClick: this._handleClick, onMouseDown: this.handleMouseDown, onTouchStart: this.handleMouseDown }))));
  }
  get element() { return this; }
  static get style() { return clDataTableHeadCellCss; }
}, [1, "cl-data-table-head-cell", {
    "cellIndex": [2, "cell-index"],
    "resize": [4],
    "rearrange": [516]
  }, [[9, "touchmove", "handleMouseMove"], [9, "mousemove", "handleMouseMove"], [9, "mouseup", "handleMouseUp"], [9, "touchend", "handleMouseUp"]]]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-data-table-head-cell", "cl-icon"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-data-table-head-cell":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClDataTableHeadCell);
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { ClDataTableHeadCell as C, defineCustomElement as d };

//# sourceMappingURL=cl-data-table-head-cell2.js.map